import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, Button, MenuItem, Modal, Paper, TextField, Typography} from "@mui/material";
import {CKEditor} from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import {useAlert} from "../context/AlertContext";
import {createCodeTask} from "../api/api";
import {CodeTaskRequest} from "../types";

interface Props {
    open: boolean;
    onClose: () => void;
    lessonId: number;
    onCreated: () => void;
}

type SupportedLang = "JAVA" | "GO" | "PYTHON" | "JAVA_CLASS" | "JAVA_METHOD" | "GO_STRUCT";

const AddCodeTaskModal = ({open, onClose, lessonId, onCreated}: Props) => {
    const {t} = useTranslation();
    const {showAlert} = useAlert();

    const [title, setTitle] = useState("");
    const [orderNum, setOrderNum] = useState<number>(1);
    const [language, setLanguage] = useState<SupportedLang>("JAVA");
    const [content, setContent] = useState("");
    const [testCases, setTestCases] = useState("");
    const [saving, setSaving] = useState(false);
    const [testCasesError, setTestCasesError] = useState<string | null>(null);
    const [starterCode, setStarterCode] = useState("");

    useEffect(() => {
        if (!open) return;

        setTitle("");
        setOrderNum(1);
        setLanguage("JAVA");
        setContent("");
        setTestCases("");
        setTestCasesError(null);
        setStarterCode("");
    }, [open]);

    const ckeditorSx = (theme: any) => ({
        mb: 2,

        "& .ck.ck-editor": {
            width: "100%",
        },

        "& .ck.ck-editor__main > .ck-editor__editable": {
            minHeight: "220px",
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.default : "#fff"
            } !important`,
            color: `${theme.palette.text.primary} !important`,
            borderColor: `${theme.palette.divider} !important`,
        },

        "& .ck-content": {
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.default : "#fff"
            } !important`,
            color: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-toolbar": {
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.paper : "#f5f5f5"
            } !important`,
            borderColor: `${theme.palette.divider} !important`,
            color: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-toolbar .ck-button, & .ck.ck-toolbar .ck-button .ck-icon": {
            color: `${theme.palette.text.primary} !important`,
            fill: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-dropdown__panel": {
            backgroundColor: `${theme.palette.background.paper} !important`,
            borderColor: `${theme.palette.divider} !important`,
            color: `${theme.palette.text.primary} !important`,
        },
    });

    const validateTestCases = (value: string): boolean => {
        const trimmed = value.trim();

        if (!trimmed) {
            setTestCasesError(t("addcodetaskmodal.validation.testCasesRequired"));
            return false;
        }

        try {
            const parsed = JSON.parse(trimmed);

            if (!Array.isArray(parsed)) {
                setTestCasesError(t("addcodetaskmodal.validation.testCasesMustBeArray"));
                return false;
            }

            if (parsed.length === 0) {
                setTestCasesError(t("addcodetaskmodal.validation.testCasesEmpty"));
                return false;
            }

            for (let i = 0; i < parsed.length; i++) {
                const tc = parsed[i];

                const isObject = tc !== null && typeof tc === "object" && !Array.isArray(tc);
                if (!isObject) {
                    setTestCasesError(
                        t("addcodetaskmodal.validation.invalidTestCaseObject", {index: i})
                    );
                    return false;
                }

                if (typeof (tc as any).input !== "string") {
                    setTestCasesError(
                        t("addcodetaskmodal.validation.invalidTestCaseInput", {index: i})
                    );
                    return false;
                }

                if (typeof (tc as any).expectedOutput !== "string") {
                    setTestCasesError(
                        t("addcodetaskmodal.validation.invalidTestCaseExpectedOutput", {index: i})
                    );
                    return false;
                }

                if (((tc as any).expectedOutput as string).trim().length === 0) {
                    setTestCasesError(
                        t("addcodetaskmodal.validation.emptyExpectedOutput", {index: i})
                    );
                    return false;
                }
            }

            setTestCasesError(null);
            return true;
        } catch {
            setTestCasesError(t("addcodetaskmodal.validation.invalidJson"));
            return false;
        }
    };

    const prettifyJson = () => {
        try {
            const parsed = JSON.parse(testCases);
            const pretty = JSON.stringify(parsed, null, 2);
            setTestCases(pretty);
            setTestCasesError(null);
        } catch {
            setTestCasesError(t("addcodetaskmodal.validation.cannotFormatJson"));
        }
    };

    const submit = async () => {
        if (!lessonId) return;

        const req: CodeTaskRequest = {
            title: title.trim(),
            content,
            testCases: testCases.trim(),
            language,
            orderNum: Number(orderNum),
            starterCode: starterCode.trim() ? starterCode : null,
        };

        if (!req.title) {
            showAlert(t("addcodetaskmodal.validation.titleRequired"), "error");
            return;
        }

        if (!req.orderNum || req.orderNum < 1) {
            showAlert(t("addcodetaskmodal.validation.orderRequired"), "error");
            return;
        }

        if (!req.content || !req.content.trim()) {
            showAlert(t("addcodetaskmodal.validation.contentRequired"), "error");
            return;
        }

        if (!validateTestCases(req.testCases)) {
            return;
        }

        try {
            setSaving(true);
            await createCodeTask(lessonId, req);
            showAlert(t("addcodetaskmodal.alerts.created"), "success");
            onClose();
            onCreated();
        } catch (e: any) {
            showAlert(e?.message ?? t("addcodetaskmodal.errors.createFailed"), "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "min(1000px, 100vw)",
                    maxHeight: "100vh",
                    overflow: "auto",
                    outline: "none",
                }}
            >
                <Paper
                    elevation={1}
                    sx={(theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 0,
                        p: 2,
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            px: 2,
                            py: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        })}
                    >
                        <Typography variant="h6">
                            {t("addcodetaskmodal.title")}
                        </Typography>
                    </Box>

                    <Box sx={{px: 2, py: 2, display: "flex", flexDirection: "column", gap: 2}}>
                        <Box sx={{display: "flex", gap: 2, flexWrap: "wrap"}}>
                            <TextField
                                label={t("addcodetaskmodal.fields.title")}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                fullWidth
                            />

                            <TextField
                                label={t("addcodetaskmodal.fields.order")}
                                type="number"
                                value={orderNum}
                                onChange={(e) => setOrderNum(Number(e.target.value))}
                                sx={{width: 160}}
                            />

                            <TextField
                                select
                                label={t("addcodetaskmodal.fields.language")}
                                value={language}
                                onChange={(e) => setLanguage(e.target.value as SupportedLang)}
                                sx={{width: 200}}
                            >
                                <MenuItem value="JAVA">JAVA</MenuItem>
                                <MenuItem value="GO">GO</MenuItem>
                                <MenuItem value="PYTHON">PYTHON</MenuItem>
                                <MenuItem value="JAVA_CLASS">JAVA_CLASS</MenuItem>
                                <MenuItem value="JAVA_METHOD">JAVA_METHOD</MenuItem>
                                <MenuItem value="GO_STRUCT">GO_STRUCT</MenuItem>
                            </TextField>
                        </Box>

                        <Typography variant="subtitle2" sx={{color: "text.secondary"}}>
                            {t("addcodetaskmodal.starterCode")}
                        </Typography>

                        <TextField
                            label={t("addcodetaskmodal.fields.starterCode")}
                            value={starterCode}
                            onChange={(e) => setStarterCode(e.target.value)}
                            multiline
                            minRows={8}
                            fullWidth
                            placeholder={t("addcodetaskmodal.placeholders.starterCode")}
                        />

                        <Typography variant="subtitle2" sx={{color: "text.secondary"}}>
                            {t("addcodetaskmodal.taskContent")}
                        </Typography>

                        <Box sx={ckeditorSx}>
                            <CKEditor
                                editor={ClassicEditor as any}
                                data={content}
                                onChange={(_event: any, editor: any) => {
                                    const html = editor.getData();
                                    setContent(html);
                                }}
                                config={{mediaEmbed: {previewsInData: true}}}
                            />
                        </Box>

                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                            <Typography variant="subtitle2" sx={{color: "text.secondary"}}>
                                {t("addcodetaskmodal.testCases")}
                            </Typography>

                            <Button
                                variant="outlined"
                                onClick={prettifyJson}
                                sx={{textTransform: "none"}}
                                disabled={saving}
                            >
                                {t("addcodetaskmodal.prettifyJson")}
                            </Button>
                        </Box>

                        <TextField
                            label={t("addcodetaskmodal.fields.testCasesExample")}
                            value={testCases}
                            onChange={(e) => {
                                const v = e.target.value;
                                setTestCases(v);
                                if (v.trim().length > 0) validateTestCases(v);
                                else setTestCasesError(null);
                            }}
                            onBlur={() => {
                                if (testCases.trim().length > 0) validateTestCases(testCases);
                            }}
                            multiline
                            minRows={10}
                            fullWidth
                            error={!!testCasesError}
                            helperText={testCasesError ?? t("addcodetaskmodal.testCasesHelper")}
                            placeholder={t("addcodetaskmodal.placeholders.testCases")}
                        />

                        <Box sx={{display: "flex", justifyContent: "flex-end", gap: 1}}>
                            <Button onClick={onClose} variant="outlined" sx={{textTransform: "none"}}>
                                {t("common.cancel")}
                            </Button>

                            <Button onClick={submit} variant="contained" disabled={saving} sx={{textTransform: "none"}}>
                                {saving ? t("addcodetaskmodal.saving") : t("common.create")}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Modal>
    );
};

export default AddCodeTaskModal;